import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import api from "@/lib/api";

/**
 * TagCombobox — a compact input that suggests existing tags as you type.
 *
 * - Fetches /api/tags on mount (list of {name, count} sorted by usage).
 * - Filters to substring matches, respects the `existing` prop to hide tags
 *   already assigned to this asset.
 * - Keyboard support: ↑/↓ to navigate, Enter to add highlighted (or create new
 *   when none match), Escape to close.
 *
 * Usage:
 *   <TagCombobox existing={tags} onAdd={(t) => setTags([...tags, t])} />
 */
export default function TagCombobox({ existing = [], onAdd, placeholder = "Add a tag…", testId = "tag-combobox-input", folderId = null }) {
  const [allTags, setAllTags] = useState([]);
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // When we're inside a folder, use the recursive `/tags/suggestions`
    // endpoint so autocomplete surfaces every tag already used anywhere in
    // this folder's project — even when the current folder is a fresh empty
    // subfolder. The strict grid-filter endpoint (`/tags`) stays untouched.
    const path = folderId ? "/tags/suggestions" : "/tags";
    const params = folderId ? { folder_id: folderId } : {};
    api.get(path, { params }).then((r) => setAllTags(r.data)).catch(() => setAllTags([]));
  }, [folderId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const normalized = value.trim().toLowerCase();
  const existingSet = useMemo(() => new Set(existing.map((x) => x.toLowerCase())), [existing]);

  const suggestions = useMemo(() => {
    const list = allTags
      .filter((t) => !existingSet.has(t.name.toLowerCase()))
      .filter((t) => (normalized ? t.name.toLowerCase().includes(normalized) : true));
    return list.slice(0, 8);
  }, [allTags, existingSet, normalized]);

  const exactMatch = suggestions.some((s) => s.name.toLowerCase() === normalized);
  const canCreateNew = normalized.length > 0 && !exactMatch && !existingSet.has(normalized);

  useEffect(() => { setHighlight(0); }, [normalized]);

  const commit = (name) => {
    const t = (name || "").trim().toLowerCase();
    if (!t || existingSet.has(t)) return;
    onAdd(t);
    setValue("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    const total = suggestions.length + (canCreateNew ? 1 : 0);
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setHighlight((h) => (total === 0 ? 0 : (h + 1) % total));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setOpen(true);
      setHighlight((h) => (total === 0 ? 0 : (h - 1 + total) % total));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight < suggestions.length) {
        commit(suggestions[highlight]?.name);
      } else if (canCreateNew) {
        commit(normalized);
      } else if (normalized) {
        commit(normalized);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const renderMatch = (name) => {
    if (!normalized) return name;
    const idx = name.toLowerCase().indexOf(normalized);
    if (idx < 0) return name;
    return (
      <>
        {name.slice(0, idx)}
        <mark className="bg-transparent text-[var(--brand-accent)] font-medium">{name.slice(idx, idx + normalized.length)}</mark>
        {name.slice(idx + normalized.length)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => { setValue(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs focus:outline-none focus:border-[var(--brand-accent)]/60 focus:ring-1 focus:ring-[var(--brand-accent)]/30 transition"
      />
      {open && (suggestions.length > 0 || canCreateNew) && (
        <div
          data-testid="tag-combobox-menu"
          className="absolute left-0 right-0 top-full mt-1 bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl z-40 py-1 max-h-64 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.name}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(s.name); }}
              onMouseEnter={() => setHighlight(i)}
              data-testid={`tag-combobox-option-${s.name}`}
              className={`w-full flex items-center justify-between gap-2 text-left px-3 py-1.5 text-xs transition ${highlight === i ? "bg-zinc-900 text-[var(--brand-accent)]" : "text-zinc-200 hover:bg-zinc-900"}`}
            >
              <span className="truncate">{renderMatch(s.name)}</span>
              <span className="text-[10px] text-zinc-600 tabular-nums shrink-0">{s.count}</span>
            </button>
          ))}
          {canCreateNew && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(normalized); }}
              onMouseEnter={() => setHighlight(suggestions.length)}
              data-testid="tag-combobox-create-new"
              className={`w-full flex items-center gap-2 text-left px-3 py-1.5 text-xs border-t border-zinc-900 transition ${highlight === suggestions.length ? "bg-zinc-900 text-[var(--brand-accent)]" : "text-zinc-400 hover:bg-zinc-900 hover:text-[var(--brand-accent)]"}`}
            >
              <Sparkles className="w-3 h-3 shrink-0" />
              <span>Create new tag: <span className="text-[var(--brand-accent)] font-medium">&ldquo;{normalized}&rdquo;</span></span>
              <Plus className="w-3 h-3 ml-auto shrink-0 opacity-60" />
            </button>
          )}
          {suggestions.length === 0 && !canCreateNew && (
            <div className="px-3 py-2 text-[11px] text-zinc-500 italic">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}
